import React from 'react';
import {createRoot} from 'react-dom/client';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';

import App from './components/App';
import config from './config';
import configureStore from './store/configureStore';
import createFetch from './createFetch';
import history from './history';
import router from './router';
import { getIntl } from './actions/intl';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';

// Universal HTTP client
const fetch = createFetch(window.fetch, {
  baseUrl: window.App.apiUrl,
});

const fetchExternal = createFetch(window.fetch, {
  baseUrl: '',
});

// Initialize a new Redux store
// http://redux.js.org/docs/basics/UsageWithReact.html
const store = configureStore(window.App.state, {
  fetch,
  fetchExternal,
  history,
});

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  dir: config.dir,
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  store,
  storeSubscription: null,
  // Universal HTTP client
  fetch,
  fetchExternal,
  // intl instance as it can be get with injectIntl
  intl: store.dispatch(getIntl()),
};

let currentLocation = history.location;
let appInstance;
let rootElement;
let root;

const scrollPositionsHistory = {};
scrollPositionsHistory[currentLocation.key] = {
  scrollX: window.pageXOffset,
  scrollY: window.pageYOffset,
};

// Re-render the app when window.location changes
async function onLocationChange(location_obj) {

  // Start of handling location from location object.
  // On page load the argument is location object but on location chage it has different arugment
  let action = null;
  if(location_obj.action){
    action = location_obj.action;
  }
  let location = location_obj;
  if(location_obj.location){
    location = location_obj.location;
  }
  // End of handling location from location object.

  // Remember the latest scroll position for the previous location
  if(location.key && action === 'PUSH'){
    scrollPositionsHistory[location.key] = {
      scrollX: window.pageXOffset,
      scrollY: window.pageYOffset,
    };
  }
  // Delete stored scroll position for next page if any
  // if (action === 'PUSH') {
  //   delete scrollPositionsHistory[location.key];
  // }
  currentLocation = location;

  context.intl = store.dispatch(getIntl());

  const isInitialRender = !action;
  try {
    context.isInitialRender = isInitialRender;
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);
    context.locale = store.getState().intl.locale;

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    if(!rootElement){
      rootElement = document.getElementById('app');
      root = createRoot(rootElement);
    }
    const css = new Set() // CSS for all rendered React components
    const insertCss = (...styles) => styles.forEach(style => style._insertCss && css.add(style._insertCss()));

    const { messages } = context.intl;

    // const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;
    appInstance = root.render(
      <Provider store={store}>
        <IntlProvider key={context.locale} locale={context.locale} messages={messages}>
          <StyleContext.Provider value={{ insertCss }}>
            <App context={context}>{route.component}</App>
          </StyleContext.Provider>
        </IntlProvider>
      </Provider>
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('We will reload your page after error');
      window.location.reload();
    }
  }
}

let isHistoryObserved = false;
export default function main() {
  // Handle client-side navigation by using HTML5 History API
  // For more information visit https://github.com/mjackson/history#readme
  currentLocation = history.location;
  if (!isHistoryObserved) {
    isHistoryObserved = true;
    history.listen(onLocationChange);
  }
  onLocationChange(currentLocation);
}

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}
